import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowLeftIcon, PencilIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import toast from '../../utils/toast';

const FormSubmissionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSubmissionDetails();
  }, [id]);

  const fetchSubmissionDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/form-submissions/${id}`);
      console.log('Submission data:', response.data);
      console.log('Submission data type:', typeof response.data.submissionData);
      console.log('Submission data keys:', response.data.submissionData ? Object.keys(response.data.submissionData) : 'No submissionData');
      
      // Handle Map objects from Mongoose
      let submissionData = response.data.submissionData;
      if (submissionData && typeof submissionData === 'object' && submissionData.constructor.name === 'Map') {
        submissionData = Object.fromEntries(submissionData);
      }
      
      setSubmission({
        ...response.data,
        submissionData: submissionData
      });
    } catch (error) {
      console.error('Error fetching submission details:', error);
      setError('Failed to load submission details');
      toast.error('Failed to load submission details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'PPpp');
    } catch (err) {
      return 'Invalid date';
    }
  };

  // Helper function to safely display values
  const getDisplayValue = (value) => {
    if (value === null || value === undefined) {
      return 'N/A';
    } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    } else if (typeof value === 'object') {
      if (value.name && value.value !== undefined) {
        return String(value.value);
      } else if (Array.isArray(value)) {
        return value.map(item => {
          if (typeof item === 'object' && item.name && item.value !== undefined) {
            return `${item.name}: ${item.value}`;
          }
          return String(item);
        }).join(', ');
      } else {
        return JSON.stringify(value);
      }
    } else {
      return String(value);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !submission) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error || 'Submission not found'}</p>
          <button
            onClick={() => navigate('/form-submissions')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Back to Form Submissions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/form-submissions')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors duration-200 shadow-sm"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Form Submission Details</h1>
                <p className="text-gray-500 text-sm font-medium">ID: {submission._id}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              {/* <button
                onClick={() => navigate(`/form-submissions/${id}/edit`)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center space-x-2"
              >
                <PencilIcon className="h-4 w-4" />
                <span>Edit</span>
              </button> */}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">

          {/* Form Information */}
          <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="px-8 py-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <div className="w-1 h-6 bg-indigo-600 rounded-full mr-3"></div>
                Form Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Form Name</p>
                  <p className="font-semibold text-gray-900">{submission.formName || submission.formId?.name || 'N/A'}</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Submission Date</p>
                  <p className="font-semibold text-gray-900">{formatDate(submission.submissionData?.submittedAt || submission.createdAt)}</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Status</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                    submission.status === 'completed' ? 'bg-green-100 text-green-800' :
                    submission.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    submission.status === 'rejected' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {submission.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Submitted By</p>
                  <p className="font-semibold text-gray-900">{submission.userName || submission.userEmail || 'N/A'}</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Deadline</p>
                  <p className="font-semibold text-gray-900">{submission.deadlineDate ? formatDate(submission.deadlineDate) : 'N/A'}</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Progress</p>
                  <p className="font-semibold text-gray-900">
                    {submission.documentProgress ? (
                      `Documents: ${submission.documentProgress.uploaded}/${submission.documentProgress.total}, Verifications: ${submission.verificationProgress.completed}/${submission.verificationProgress.total}`
                    ) : (
                      'Progress information not available'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Client Information */}
          {submission.masterDataId && (
            <div className="border-b border-gray-200">
              <div className="px-8 py-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <div className="w-1 h-6 bg-blue-600 rounded-full mr-3"></div>
                  Client Information
                </h3>
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-lg p-4 border border-blue-100 shadow-sm">
                      <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Client Name</p>
                      <p className="font-semibold text-gray-900">{submission.masterDataId.clientName}</p>
                    </div>
                    {submission.masterDataId.clientAddress && (
                      <div className="bg-white rounded-lg p-4 border border-blue-100 shadow-sm">
                        <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Client Address</p>
                        <p className="font-semibold text-gray-900">{submission.masterDataId.clientAddress}</p>
                      </div>
                    )}
                    {submission.masterDataId.clientEmail && (
                      <div className="bg-white rounded-lg p-4 border border-blue-100 shadow-sm">
                        <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Client Email</p>
                        <p className="font-semibold text-gray-900">{submission.masterDataId.clientEmail}</p>
                      </div>
                    )}
                    {submission.masterDataId.clientPhone && (
                      <div className="bg-white rounded-lg p-4 border border-blue-100 shadow-sm">
                        <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Client Phone</p>
                        <p className="font-semibold text-gray-900">{submission.masterDataId.clientPhone}</p>
                      </div>
                    )}
                    {submission.masterDataId.contactPersons && submission.masterDataId.contactPersons.length > 0 && (
                      <div className="md:col-span-2 bg-white rounded-lg p-4 border border-blue-100 shadow-sm">
                        <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-3">Contact Persons</p>
                        <div className="space-y-3">
                          {submission.masterDataId.contactPersons.map((contact, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div>
                                <span className="font-semibold text-gray-900">{contact.title} {contact.name}</span>
                                <span className="text-gray-600 ml-2"> - {contact.email}</span>
                              </div>
                              {contact.phoneNumbers && contact.phoneNumbers.length > 0 && (
                                <span className="text-sm text-gray-500">({contact.phoneNumbers.join(', ')})</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {submission.masterDataId.authorizedSignatories && submission.masterDataId.authorizedSignatories.length > 0 && (
                      <div className="md:col-span-2 bg-white rounded-lg p-4 border border-blue-100 shadow-sm">
                        <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-3">Authorized Signatories</p>
                        <div className="space-y-3">
                          {submission.masterDataId.authorizedSignatories.map((signatory, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div>
                                <span className="font-semibold text-gray-900">{signatory.title} {signatory.designationName}</span>
                                {signatory.fatherName && (
                                  <span className="text-gray-600 ml-2"> - {signatory.fatherName}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Form Fields */}
          {submission.submissionData?.formFields?.length > 0 && (
            <div className="border-b border-gray-200">
              <div className="px-8 py-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <div className="w-1 h-6 bg-green-600 rounded-full mr-3"></div>
                  Form Fields
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {submission.submissionData.formFields.map((field, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{field.field?.label || field.name}</p>
                      <p className="font-semibold text-gray-900">{field.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* General Submission Data */}
          {submission.submissionData && 
           typeof submission.submissionData === 'object' && 
           Object.keys(submission.submissionData).length > 0 && (
            <div className="border-b border-gray-200">
              <div className="px-8 py-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <div className="w-1 h-6 bg-purple-600 rounded-full mr-3"></div>
                  Form Data
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(submission.submissionData).map(([key, value]) => {
                    // Skip certain keys that are handled elsewhere
                    if (key === 'formFields' || key === 'products' || key === 'submittedAt') {
                      return null;
                    }

                    const displayValue = getDisplayValue(value);

                    return (
                      <div key={key} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </p>
                        <p className="font-semibold text-gray-900">{displayValue}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Products */}
          <div className="px-8 py-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <div className="w-1 h-6 bg-orange-600 rounded-full mr-3"></div>
              Products
            </h3>
            <div className="space-y-6">
              
              {/* Handle products from submissionData.products */}
              {submission.submissionData?.products?.map((product, index) => (
                <div key={index} className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-6">
                  <div className="bg-white rounded-lg p-6 border border-orange-100 shadow-sm">
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">{product.name}</h4>
                    <p className="text-sm text-gray-600 mb-6">{product.description}</p>

                    {/* Required Fields */}
                    <div className="mb-6">
                      <h5 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Required Fields</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {product.requiredFields.map((field, fieldIndex) => (
                          <div key={fieldIndex} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{field.label}</p>
                            {field.fieldType === 'file' ? (
                              <a href={field.value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 font-medium hover:underline">
                                View File
                              </a>
                            ) : (
                              <p className="font-semibold text-gray-900">{field.value}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Fees */}
                    <div className="mb-6">
                      <h5 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Fees</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                          <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">{product.fees.governmentFees.name}</p>
                          <p className="font-semibold text-gray-900">${product.fees.governmentFees.amount.toFixed(2)}</p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">{product.fees.professionalFees.name}</p>
                          <p className="font-semibold text-gray-900">${product.fees.professionalFees.amount.toFixed(2)}</p>
                        </div>
                        {product.fees.otherFees.map((fee, feeIndex) => (
                          <div key={feeIndex} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{fee.name}</p>
                            <p className="font-semibold text-gray-900">${fee.amount.toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex justify-between items-center bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                          <p className="text-sm font-semibold text-indigo-700 uppercase tracking-wide">Total</p>
                          <p className="text-xl font-bold text-indigo-900">${product.totalFees?.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Documents */}
                    <div className="mb-6">
                      <h5 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Documents</h5>
                      <div className="space-y-3">
                        {product.documents?.map((doc, docIndex) => (
                          <div key={docIndex} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{doc.requirementName}</p>
                            <a 
                              href={doc.document.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 font-medium hover:underline"
                            >
                              {doc.document.originalName}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Verifications */}
                    <div>
                      <h5 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Verifications</h5>
                      <div className="space-y-3">
                        {product.verifications?.map((verification, verIndex) => (
                          <div key={verIndex} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                  {verification.verificationType?.label || `Verification ${verIndex + 1}`}
                                </p>
                                <p className="font-semibold text-gray-900">{verification.value}</p>
                              </div>
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                verification.status === 'approved' ? 'bg-green-100 text-green-800' :
                                verification.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                verification.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {verification.status.replace('_', ' ')}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Handle products from productsData */}
              {submission.productsData?.map((product, index) => (
                <div key={index} className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-6">
                  <div className="bg-white rounded-lg p-6 border border-orange-100 shadow-sm">
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      {product.id && typeof product.id === 'object' ? product.id.name : `Product ${index + 1}`}
                    </h4>
                    
                    {/* Product Description */}
                    {product.id && typeof product.id === 'object' && product.id.description && (
                      <p className="text-sm text-gray-600 mb-6">{product.id.description}</p>
                    )}

                    {/* Required Fields */}
                    {product.requiredFields && product.requiredFields.length > 0 && (
                      <div className="mb-6">
                        <h5 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Required Fields</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {product.requiredFields.map((field, fieldIndex) => (
                            <div key={fieldIndex} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{field.label}</p>
                              {field.fieldType === 'file' ? (
                                <a href={field.value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 font-medium hover:underline">
                                  View File
                                </a>
                              ) : (
                                <p className="font-semibold text-gray-900">{field.value}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Fees */}
                    {product.fees && (
                      <div className="mb-6">
                        <h5 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Fees</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {product.fees.governmentFees && (
                            <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                              <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">{product.fees.governmentFees.name}</p>
                              <p className="font-semibold text-gray-900">${product.fees.governmentFees.amount?.toFixed(2) || '0.00'}</p>
                            </div>
                          )}
                          {product.fees.professionalFees && (
                            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">{product.fees.professionalFees.name}</p>
                              <p className="font-semibold text-gray-900">${product.fees.professionalFees.amount?.toFixed(2) || '0.00'}</p>
                            </div>
                          )}
                          {product.fees.otherFees && product.fees.otherFees.map((fee, feeIndex) => (
                            <div key={feeIndex} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{fee.name}</p>
                              <p className="font-semibold text-gray-900">${fee.amount?.toFixed(2) || '0.00'}</p>
                            </div>
                          ))}
                        </div>
                        {product.totalFees && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex justify-between items-center bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                              <p className="text-sm font-semibold text-indigo-700 uppercase tracking-wide">Total</p>
                              <p className="text-xl font-bold text-indigo-900">${product.totalFees?.toFixed(2)}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Documents */}
                    {product.documents && product.documents.length > 0 && (
                      <div className="mb-6">
                        <h5 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Documents</h5>
                        <div className="space-y-3">
                          {product.documents.map((doc, docIndex) => (
                            <div key={docIndex} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{doc.requirementName}</p>
                              <a 
                                href={doc.document.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 font-medium hover:underline"
                              >
                                {doc.document.originalName}
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Verifications */}
                    {product.verifications && product.verifications.length > 0 && (
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Verifications</h5>
                        <div className="space-y-3">
                          {product.verifications.map((verification, verIndex) => (
                            <div key={verIndex} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                    {verification.verificationType?.label || `Verification ${verIndex + 1}`}
                                  </p>
                                  <p className="font-semibold text-gray-900">{verification.value}</p>
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                  verification.status === 'approved' ? 'bg-green-100 text-green-800' :
                                  verification.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                  verification.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {verification.status.replace('_', ' ')}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormSubmissionDetails; 